/* PERCUP PDP v2 core — single module.
   All price updates flow through a central state object so pill clicks,
   variant changes, and qty adjustments keep every price display in sync.
*/
(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }

  function formatMoney(cents, sym) {
    if (isNaN(cents)) return '';
    sym = sym || window.pdp2Currency || 'Rs.';
    var v = (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return sym + ' ' + v;
  }

  function init(root) {
    if (!root || root.dataset.pdp2Wired === '1') return;
    root.dataset.pdp2Wired = '1';

    // Run BEFORE anything else so downstream queries see the corrected DOM.
    rescueLayout(root);


    // ── Central state ────────────────────────────────────────────────
    var state = {
      currencySym: extractCurrencySymbol(root),
      unitCents: extractUnitCents(root),        // current variant's active-currency cents
      compareCents: extractCompareCents(root),
      qty: 1,
      purchaseType: 'onetime',
      planId: '',
      planName: '',
      subUnitCents: null,
      saveUnitCents: 0,
      variantsCache: null,
      plansCache: collectPlans(root),
      // Shopify.currency.rate converts SHOP BASE (USD) → ACTIVE currency.
      // To convert back to USD (for points), divide by this rate.
      usdRate: parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1,
    };
    root.__pdp2State = state;

    // Fill loyalty points in USD immediately after init (Liquid's initial
    // render used active-currency cents which is wrong for USD-based points).
    renderLoyalty(root, state);

    // Seed the ATC price with unit cents so qty changes work immediately
    var ctaPriceEl = root.querySelector('[data-pdp2-cta-price]');
    if (ctaPriceEl) ctaPriceEl.setAttribute('data-unit-cents', String(state.unitCents));

    wirePurchaseToggle(root, state);
    wireFrequencyPills(root, state);
    wireQty(root, state);
    wireVariantChange(root, state);
    wireTabs(root);
    wirePrepMethods(root);
    wireShipAccordion(root);
    wireKeyInfo(root);
    wireSEO(root);
    wireGallery(root);
    wireStickyHeaderOffset(root);
    wireStickyATC(root, state);
    wireReviewsHook(root);
    wireCsCarousel(root);
    wireCsRecommendations(root);
    wireCsTooltips(root);
    wireNotifyModal(root);

    // DO NOT call renderPrices on init for one-time products — Liquid already
    // rendered everything correctly. We only mutate prices on user interaction
    // (qty/variant/pill). EXCEPTION: for subscription-only products
    // (`requires_selling_plan` = true → `data-pdp2-sub-only` on the group),
    // Subscribe is preselected, so the stickies + CTA need the sub price now.
    if (root.querySelector('[data-pdp2-sub-only]')) {
      ensurePlansHydrated(root, state, function () { renderPrices(root, state); });
      var planInput = root.querySelector('[data-pdp2-selling-plan]');
      if (planInput && state.planId) planInput.value = state.planId;
    }
  }

  // ══════════════════════════════════════════════════════════════════
  // Rescue layout when user-authored metafield HTML (e.g. the SEO
  // "Know more about this product" body, which is raw HTML pasted by
  // the merchant) contains unbalanced </div> tags. Extra closes can
  // bubble out of .pdp2-seo__body, then .pdp2-seo, then .pdp2-info —
  // which makes .pdp2-cs (You might also like) and any later siblings
  // render as direct children of .pdp2-pdp instead of inside .pdp2-info.
  // In a 2-column grid, those orphans then get placed in column 1 of
  // a new row, breaking the right-rail layout entirely.
  //
  // Fix: scope to this section's product-info root, and reparent any
  // direct child of .pdp2-pdp that isn't the gallery or the info column
  // back into .pdp2-info, preserving source order.
  // ══════════════════════════════════════════════════════════════════
  function rescueLayout(root) {
    var pdp = root.querySelector(':scope > .pdp2-pdp');
    if (!pdp) return;
    var info = pdp.querySelector(':scope > .pdp2-info');
    if (!info) return;
    var orphans = [];
    for (var i = 0; i < pdp.children.length; i++) {
      var child = pdp.children[i];
      if (child === info) continue;
      if (child.classList.contains('pdp2-img-col')) continue;
      orphans.push(child);
    }
    orphans.forEach(function (o) { info.appendChild(o); });
  }

  // ══════════════════════════════════════════════════════════════════
  // Extract initial values from the DOM
  // ══════════════════════════════════════════════════════════════════
  function extractCurrencySymbol(root) {
    var priceEl = root.querySelector('.pdp2-price');
    if (!priceEl) return '';
    // Take everything up to the first digit as the prefix; fall back to empty.
    var m = (priceEl.textContent || '').match(/^(\D+?)(?=\d)/);
    return m ? m[1].trim() : '';
  }
  function extractUnitCents(root) {
    // Authoritative source: the data attribute emitted by Liquid.
    var el = root.querySelector('[data-pdp2-cta-price]');
    if (el) {
      var n = parseInt(el.getAttribute('data-unit-cents') || '0', 10);
      if (!isNaN(n) && n > 0) return n;
    }
    return 0;
  }
  function extractCompareCents(root) {
    // Liquid doesn't emit compare cents directly; derive lazily from variant
    // data when we fetch /products/<handle>.js on first interaction.
    return 0;
  }
  function formatSafe(cents, state) {
    if (!cents || isNaN(cents) || cents <= 0) return null;
    return formatMoney(cents, state.currencySym);
  }

  // ══════════════════════════════════════════════════════════════════
  // Loyalty points — always computed in USD so points are stable across
  // customer geolocations. unitCents is in active currency; dividing by
  // Shopify.currency.rate gives base-currency (USD) cents, then /100 = USD
  // dollars = points.
  // ══════════════════════════════════════════════════════════════════
  // Variant-ID → USD price cache (loaded once per page via Storefront GraphQL)
  var USD_PRICES = null;  // { "<variantId>": usdAmount }

  function fetchUsdPrices(handle) {
    if (USD_PRICES) return Promise.resolve(USD_PRICES);
    var tokenMatch = document.documentElement.outerHTML.match(/"accessToken":"([a-f0-9]{32,})"/);
    var token = tokenMatch ? tokenMatch[1] : null;
    if (!token) return Promise.resolve({});
    var endpoint = (window.Shopify && window.Shopify.shop)
      ? 'https://' + window.Shopify.shop + '/api/2024-10/graphql.json'
      : '/api/2024-10/graphql.json';
    var body = {
      query: 'query @inContext(country: US) { product(handle: "' + handle + '") { variants(first: 50) { nodes { id price { amount } } } } }'
    };
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); }).then(function (resp) {
      var map = {};
      var nodes = resp && resp.data && resp.data.product && resp.data.product.variants && resp.data.product.variants.nodes;
      if (nodes) {
        nodes.forEach(function (v) {
          var id = (v.id || '').replace('gid://shopify/ProductVariant/', '');
          map[id] = parseFloat(v.price.amount);
        });
      }
      USD_PRICES = map;
      return map;
    }).catch(function () { return {}; });
  }

  function renderLoyalty(root, state) {
    if (!state || !state.unitCents) return;

    // Priority 1: live USD price fetched from Shopify Storefront GraphQL
    //   (@inContext(country: US) forces USD regardless of visitor market).
    // Priority 2 (fallback while GraphQL is in-flight): active-currency cents
    //   divided by Shopify.currency.rate. Approximate — ignores Shopify
    //   Markets price-adjustment markup, so may be off a few percent until
    //   the GraphQL response lands.
    var variantInput = root.querySelector('[data-pdp2-variant-id]');
    var variantId = variantInput ? variantInput.value : null;
    var applyPoints = function (usdUnit) {
      var totalPts = usdUnit > 0 ? Math.round(usdUnit * state.qty) : 0;
      var stickyLoyalty = root.querySelector('.pdp2-sticky__lbl');
      if (stickyLoyalty) stickyLoyalty.textContent = 'Earn ' + totalPts + ' PERCUP points';
      var mainLoyalty = root.querySelector('.pdp2-loyalty span');
      if (mainLoyalty) mainLoyalty.textContent = totalPts + ' PERCUP points';
    };

    if (USD_PRICES && variantId && USD_PRICES[variantId] != null) {
      applyPoints(USD_PRICES[variantId]);
      return;
    }

    // Kick off GraphQL fetch (async); apply approximate points meanwhile
    var handle = root.getAttribute('data-product-handle');
    if (handle) {
      fetchUsdPrices(handle).then(function (map) {
        if (variantId && map[variantId] != null) applyPoints(map[variantId]);
      });
    }

    var usdCents = state.unitCents / (state.usdRate || 1);
    applyPoints(usdCents / 100);
  }

  // Collect selling plans and their per-variant price allocations from the
  // DOM. Each pill button carries a data-plan-id; we look up the plan's
  // discount by reading price_adjustments embedded via data-plan-* OR, if
  // absent, by fetching /products/<handle>.js once on first interaction.
  function collectPlans(root) {
    var plans = {};
    root.querySelectorAll('.pdp2-freq-btn').forEach(function (btn) {
      var id = btn.getAttribute('data-plan-id');
      if (!id) return;
      plans[id] = {
        id: id,
        name: btn.getAttribute('data-plan-name') || btn.textContent.trim(),
        // Filled in lazily from product.js
        discount_type: null,
        discount_value: null,
      };
    });
    return plans;
  }

  function ensurePlansHydrated(root, state, cb) {
    var anyMissing = Object.values(state.plansCache).some(function (p) { return p.discount_type == null; });
    if (!anyMissing) { cb(); return; }
    var handle = root.getAttribute('data-product-handle');
    if (!handle) { cb(); return; }
    fetch('/products/' + handle + '.js').then(function (r) { return r.json(); }).then(function (p) {
      state.variantsCache = p.variants;
      // Walk selling_plan_groups → selling_plans → price_adjustments
      (p.selling_plan_groups || []).forEach(function (g) {
        (g.selling_plans || []).forEach(function (plan) {
          var adj = (plan.price_adjustments || [])[0] || {};
          var entry = state.plansCache[String(plan.id)];
          if (entry) {
            entry.discount_type = adj.value_type || 'percentage';
            entry.discount_value = adj.value || 15;
          }
        });
      });
      cb();
    }).catch(function () { cb(); });
  }

  function computePlanSub(state, planId) {
    var plan = state.plansCache[String(planId)];
    if (!plan || !plan.discount_type) {
      // default 15% fallback
      var save15 = Math.round(state.unitCents * 0.15);
      return { subCents: state.unitCents - save15, saveCents: save15 };
    }
    var save;
    if (plan.discount_type === 'percentage') {
      save = Math.round(state.unitCents * (plan.discount_value / 100));
    } else if (plan.discount_type === 'fixed_amount' || plan.discount_type === 'fixed') {
      save = plan.discount_value;  // cents
    } else if (plan.discount_type === 'price') {
      // discount_value is the new price
      save = state.unitCents - plan.discount_value;
    } else {
      save = Math.round(state.unitCents * 0.15);
    }
    if (save < 0) save = 0;
    if (save > state.unitCents) save = state.unitCents;
    return { subCents: state.unitCents - save, saveCents: save };
  }

  // ══════════════════════════════════════════════════════════════════
  // Central price renderer — reads state, writes DOM
  // ══════════════════════════════════════════════════════════════════
  function renderPrices(root, state) {
    // Safety guard: if we don't have a valid unit cents, bail rather than
    // corrupt the Liquid-rendered prices.
    if (!state.unitCents || state.unitCents <= 0 || isNaN(state.unitCents)) return;

    var sym = state.currencySym;
    var fmt = function (c) { return formatSafe(c, state) || ''; };

    // Main price
    var mainPrice = root.querySelector('.pdp2-price');
    if (mainPrice) mainPrice.textContent = fmt(state.unitCents);
    var compareEl = root.querySelector('.pdp2-price-compare');
    if (compareEl) {
      if (state.compareCents > state.unitCents) {
        compareEl.textContent = fmt(state.compareCents);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // One Time Purchase price row
    var onetimePrice = root.querySelector('[data-pdp2-purchase="onetime"] .pdp2-pur-price');
    if (onetimePrice) onetimePrice.textContent = fmt(state.unitCents);

    // Subscribe price row (reflects selected plan)
    var subPriceEl = root.querySelector('[data-pdp2-sub-price]');
    var subStrikeEl = root.querySelector('[data-pdp2-purchase="subscribe"] .pdp2-pur-strike');
    if (state.planId && state.plansCache[state.planId]) {
      var r = computePlanSub(state, state.planId);
      state.subUnitCents = r.subCents;
      state.saveUnitCents = r.saveCents;
      if (subPriceEl && r.subCents > 0) subPriceEl.textContent = fmt(r.subCents);
      if (subStrikeEl) subStrikeEl.textContent = fmt(state.unitCents);
    }

    // Price preview box
    var ppPriceEl = root.querySelector('[data-pdp2-pp-price]');
    var ppFreqEl = root.querySelector('[data-pdp2-pp-freq]');
    var ppSaveEl = root.querySelector('.pdp2-pp-save');
    if (ppPriceEl && state.subUnitCents) ppPriceEl.textContent = fmt(state.subUnitCents);
    if (ppSaveEl && state.saveUnitCents) ppSaveEl.textContent = 'Save ' + fmt(state.saveUnitCents);
    if (ppFreqEl && state.planName) ppFreqEl.textContent = state.planName.toLowerCase();

    // ATC button price — qty × (unit OR sub unit if subscribed)
    var ctaPrice = root.querySelector('[data-pdp2-cta-price]');
    if (ctaPrice) {
      var unit = state.purchaseType === 'subscribe' && state.subUnitCents
        ? state.subUnitCents
        : state.unitCents;
      ctaPrice.textContent = fmt(unit * state.qty);
      ctaPrice.setAttribute('data-unit-cents', String(unit));
    }

    // Sticky bar price (desktop top bar + mobile bottom bar)
    var stickyPrices = root.querySelectorAll('[data-pdp2-sticky-price]');
    if (stickyPrices.length) {
      var unitS = state.purchaseType === 'subscribe' && state.subUnitCents
        ? state.subUnitCents
        : state.unitCents;
      var stickyText = fmt(unitS * state.qty);
      stickyPrices.forEach(function (el) { el.textContent = stickyText; });
    }

    // Loyalty points — always in USD, derived from active-currency price
    renderLoyalty(root, state);

    // Savings benefit sub-text
    var benSaveSub = root.querySelector('.pdp2-bens .pdp2-ben:first-child .pdp2-ben-sub');
    if (benSaveSub && state.saveUnitCents) {
      benSaveSub.textContent = 'Save ' + fmt(state.saveUnitCents) + ' per delivery';
    }

    // SAVE X% tag + "FLAT X% OFF" benefit headline — derived from active
    // plan's save/unit ratio so they track frequency-pill changes.
    if (state.saveUnitCents && state.unitCents) {
      var pct = Math.round((state.saveUnitCents / state.unitCents) * 100);
      var saveTagEl = root.querySelector('.pdp2-save-tag');
      if (saveTagEl) saveTagEl.textContent = 'SAVE ' + pct + '%';
      var benFlatEm = root.querySelector('.pdp2-bens .pdp2-ben:first-child .pdp2-ben-text em');
      if (benFlatEm) benFlatEm.textContent = 'FLAT ' + pct + '% OFF';
    }
  }

  // ══════════════════════════════════════════════════════════════════
  // Purchase toggle (One Time / Subscribe)
  // ══════════════════════════════════════════════════════════════════
  function wirePurchaseToggle(root, state) {
    var group = root.querySelector('[data-pdp2-purchase-group]');
    if (!group) return;
    var subBox = root.querySelector('[data-pdp2-sub-box]');
    var planInput = root.querySelector('[data-pdp2-selling-plan]');

    function apply(selected) {
      state.purchaseType = selected;
      group.querySelectorAll('[data-pdp2-purchase]').forEach(function (opt) {
        var on = opt.getAttribute('data-pdp2-purchase') === selected;
        opt.classList.toggle('on', on);
        var radio = opt.querySelector('input[type="radio"]');
        if (radio) radio.checked = on;
      });
      if (subBox) subBox.hidden = false;
      if (planInput) {
        planInput.value = selected === 'subscribe' ? state.planId : '';
      }
      ensurePlansHydrated(root, state, function () { renderPrices(root, state); });
    }

    group.querySelectorAll('[data-pdp2-purchase]').forEach(function (opt) {
      opt.addEventListener('click', function (e) {
        if (e.target.tagName === 'INPUT') return;
        apply(opt.getAttribute('data-pdp2-purchase'));
      });
      var radio = opt.querySelector('input[type="radio"]');
      if (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) apply(opt.getAttribute('data-pdp2-purchase'));
        });
      }
    });

    // Seed initial state from current DOM "on" class
    var active = group.querySelector('.pdp2-pur-opt.on');
    if (active) state.purchaseType = active.getAttribute('data-pdp2-purchase') || 'onetime';
  }

  // ══════════════════════════════════════════════════════════════════
  // Frequency pills
  // ══════════════════════════════════════════════════════════════════
  function wireFrequencyPills(root, state) {
    var wrap = root.querySelector('[data-pdp2-freq-opts]');
    if (!wrap) return;
    var planInput = root.querySelector('[data-pdp2-selling-plan]');

    function selectPill(btn) {
      wrap.querySelectorAll('.pdp2-freq-btn').forEach(function (b) { b.classList.remove('on'); });
      btn.classList.add('on');
      state.planId = btn.getAttribute('data-plan-id') || '';
      state.planName = btn.getAttribute('data-plan-name') || (btn.textContent || '').trim();
      if (planInput && state.purchaseType === 'subscribe') planInput.value = state.planId;
      ensurePlansHydrated(root, state, function () { renderPrices(root, state); });
    }

    wrap.querySelectorAll('.pdp2-freq-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Interacting with a frequency pill implies the user wants to
        // subscribe. If One Time is currently selected, flip over to
        // Subscribe & Save automatically so the choice is reflected in
        // the active state and form submission.
        if (state.purchaseType !== 'subscribe') {
          var subOpt = root.querySelector('[data-pdp2-purchase="subscribe"]');
          if (subOpt) subOpt.click();
        }
        selectPill(btn);
      });
    });

    // Seed from currently-on pill
    var initial = wrap.querySelector('.pdp2-freq-btn.on') || wrap.querySelector('.pdp2-freq-btn');
    if (initial) {
      state.planId = initial.getAttribute('data-plan-id') || '';
      state.planName = initial.getAttribute('data-plan-name') || (initial.textContent || '').trim();
    }
  }

  // ══════════════════════════════════════════════════════════════════
  // Qty stepper
  // ══════════════════════════════════════════════════════════════════
  function wireQty(root, state) {
    var inputs = root.querySelectorAll('[data-pdp2-qty-val]');
    if (!inputs.length) return;
    state.qty = parseInt(inputs[0].value, 10) || 1;

    function apply(next) {
      state.qty = Math.max(1, next);
      inputs.forEach(function (el) { if (el.value !== String(state.qty)) el.value = state.qty; });
      renderPrices(root, state);
    }

    root.querySelectorAll('[data-pdp2-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var delta = parseInt(btn.getAttribute('data-pdp2-qty'), 10) || 1;
        apply(state.qty + delta);
      });
    });
    inputs.forEach(function (input) {
      input.addEventListener('change', function () { apply(parseInt(input.value, 10) || 1); });
      input.addEventListener('input',  function () { apply(parseInt(input.value, 10) || 1); });
    });
  }

  // ══════════════════════════════════════════════════════════════════
  // Variant change — update variant ID, price, all displays
  // ══════════════════════════════════════════════════════════════════
  function wireVariantChange(root, state) {
    var selects = root.querySelectorAll('[data-pdp2-option-index]');
    if (selects.length === 0) return;

    function fetchVariants() {
      if (state.variantsCache) return Promise.resolve(state.variantsCache);
      var handle = root.getAttribute('data-product-handle');
      if (!handle) return Promise.resolve(null);
      return fetch('/products/' + handle + '.js').then(function (r) { return r.json(); }).then(function (p) {
        state.variantsCache = p.variants;
        // Also hydrate plans while we have the data
        (p.selling_plan_groups || []).forEach(function (g) {
          (g.selling_plans || []).forEach(function (plan) {
            var adj = (plan.price_adjustments || [])[0] || {};
            var entry = state.plansCache[String(plan.id)];
            if (entry) {
              entry.discount_type = adj.value_type || 'percentage';
              entry.discount_value = adj.value || 15;
            }
          });
        });
        return p.variants;
      }).catch(function () { return null; });
    }

    function onChange() {
      var values = Array.from(selects).map(function (s) { return s.value; });
      // Disable ATC + sticky CTAs while we resolve the variant (price /
      // availability / gallery). Re-enabled in toggleAvailability once
      // we know the new variant's state.
      setLoading(root, true);
      fetchVariants().then(function (variants) {
        if (!variants) { setLoading(root, false); return; }
        var match = variants.find(function (v) {
          return v.options.every(function (ov, idx) { return ov === values[idx]; });
        });
        if (!match) { setLoading(root, false); return; }
        state.unitCents = match.price;
        state.compareCents = match.compare_at_price || 0;
        var variantInput = root.querySelector('[data-pdp2-variant-id]');
        if (variantInput) variantInput.value = match.id;
        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          history.replaceState({}, '', url.toString());
        }
        renderPrices(root, state);
        toggleAvailability(root, match);
        filterGalleryForVariant(root, match.id, state);
        // Update the variant pill next to the price (Acid Yellow / NO STRAP).
        // Only applies when the pill is variant-driven — products that source
        // it from a static size metafield omit the data flag and stay put.
        var weightPill = root.querySelector('[data-pdp2-weight-dynamic]');
        if (weightPill) {
          var pillText = match.public_title || match.title || values.join(' / ');
          if (pillText && pillText !== 'Default Title') {
            weightPill.textContent = '· ' + pillText;
            weightPill.removeAttribute('hidden');
          } else {
            weightPill.setAttribute('hidden', '');
          }
        }
        setLoading(root, false);
      });
    }
    selects.forEach(function (s) { s.addEventListener('change', onChange); });
    wireSwatches(root, onChange);

    // Initial-load filter: apply the gallery filter to whatever variant
    // is in the URL (?variant=XXX) right away, so the customer sees only
    // the active color's images even before they touch a swatch. If no
    // variant is in the URL, falls back to the first variant.
    var initialVid = (function () {
      try {
        var url = new URL(window.location.href);
        var v = url.searchParams.get('variant');
        if (v) return v;
      } catch (_) {}
      var input = root.querySelector('[data-pdp2-variant-id]');
      return input && input.value;
    })();
    if (initialVid) filterGalleryForVariant(root, initialVid, state);
  }

  // Filter the gallery by ALT text matching variant option values.
  // This mirrors the old PDP (product-media-slider.liquid) which the
  // merchant authored against. The convention: an image's alt text
  // contains the variant option value (e.g. alt="Sienna" or
  // alt="Carter Cold Tumbler — Sienna 590ml") to associate it with that
  // variant. A single variant can have multiple images — they all share
  // the alt with the option value.
  //
  // If no thumb's alt matches any option value, we fall back to showing
  // every thumb (better than a blank gallery).
  function filterGalleryForVariant(root, variantId, state) {
    var thumbs = Array.from(root.querySelectorAll('[data-pdp2-thumb]'));
    var dots = Array.from(root.querySelectorAll('[data-pdp2-dot]'));
    if (!thumbs.length) return;

    // Build the active variant's option-value list.
    var values = [];
    if (state && state.variantsCache) {
      var v = state.variantsCache.find(function (x) { return String(x.id) === String(variantId); });
      if (v && v.options) values = v.options.slice();
    }
    if (values.length === 0) {
      // No variant data yet; read from the visible selects as a fallback.
      Array.from(root.querySelectorAll('select[data-pdp2-option-index]')).forEach(function (s) {
        if (s.value) values.push(s.value);
      });
    }
    if (values.length === 0) return; // can't filter — leave gallery as-is

    // 0. Variant-id linkage from admin (authoritative source — the
    // merchant's explicit "this image belongs to this variant" data from
    // Shopify's "Edit variant images" panel). Always honoured when present.
    // Filename / alt-text matches below are added on top of these so a
    // partially-linked product still gets a rich gallery. As the merchant
    // links more images in admin, the heuristic naturally becomes redundant.
    var vidStr = String(variantId);
    var variantLinkedIndices = [];
    thumbs.forEach(function (t, idx) {
      var vids = (t.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
      if (vids.indexOf(vidStr) !== -1) variantLinkedIndices.push(idx);
    });

    var lcValues = values.map(function (s) { return String(s).toLowerCase(); });

    // Union variant-linked indices with alt-text matches. Both are
    // explicit merchant data: variant-linkage from "Edit variant images"
    // in admin, and alt-text from the image's alt attribute. Bidirectional
    // substring match handles size-prefixed option values (e.g. value
    // "650ML CLAY BEIGE" still matches alt "Clay Beige") without forcing
    // the merchant to bake the size prefix into every alt.
    var matchIndices = variantLinkedIndices.slice();
    thumbs.forEach(function (t, idx) {
      if (matchIndices.indexOf(idx) !== -1) return;
      var alt = (t.getAttribute('data-media-alt') || '').toLowerCase();
      if (!alt) return;
      var altHit = lcValues.some(function (v) {
        return alt.indexOf(v) !== -1 || v.indexOf(alt) !== -1;
      });
      if (altHit) matchIndices.push(idx);
    });
    matchIndices.sort(function (a, b) { return a - b; });

    // Nothing matched at all → show everything (better than a blank gallery).
    if (matchIndices.length === 0) {
      thumbs.forEach(function (t) { t.style.display = ''; });
      dots.forEach(function (d) { d.style.display = ''; });
      return;
    }

    thumbs.forEach(function (t, idx) {
      var visible = matchIndices.indexOf(idx) !== -1;
      t.style.display = visible ? '' : 'none';
    });
    dots.forEach(function (d, idx) {
      var visible = matchIndices.indexOf(idx) !== -1;
      d.style.display = visible ? '' : 'none';
    });

    // Lead with the first matching thumb's image.
    var leadIdx = matchIndices[0];
    var leadThumb = thumbs[leadIdx];
    if (leadThumb) {
      var main = root.querySelector('[data-pdp2-main-image]');
      var src = leadThumb.getAttribute('data-src');
      if (main && src && main.src !== src) {
        if (main.hasAttribute('srcset')) {
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
        }
        main.src = src;
      }
      thumbs.forEach(function (t) { t.classList.toggle('on', t === leadThumb); });
      dots.forEach(function (d, idx) {
        d.classList.toggle('on', idx === leadIdx);
      });
    }
  }

  // Color swatches: clicking a swatch sets the hidden <select> and triggers
  // the existing onChange flow. Visual `.on` state moves to the clicked swatch.
  function wireSwatches(root, onChange) {
    var swatches = root.querySelectorAll('.pdp2-swatch');
    if (!swatches.length) return;
    swatches.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (btn.classList.contains('pdp2-swatch--soldout')) {
          // Still allow selecting a sold-out variant so the Notify Me path triggers.
        }
        var idx = btn.getAttribute('data-pdp2-swatch-option-index');
        var val = btn.getAttribute('data-pdp2-swatch-value');
        var sel = root.querySelector('select[data-pdp2-option-index="' + idx + '"]');
        if (!sel) return;
        sel.value = val;
        // Visual state on swatch row.
        var group = btn.parentElement;
        if (group) {
          group.querySelectorAll('.pdp2-swatch').forEach(function (b) {
            var on = b === btn;
            b.classList.toggle('pdp2-swatch--on', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
          });
        }
        // Update inline selected-label, if rendered next to the option name.
        var row = btn.closest('[data-pdp2-variant-row]');
        var label = row && row.querySelector('[data-pdp2-selected-label]');
        if (label) label.textContent = val;
        // Dispatch a real change event so every listener (our onChange,
        // plus any third-party widgets like Appstle) reacts. This is AJAX —
        // no page reload, just price/URL/availability updates in place.
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  // While a variant change is in flight (fetching /products/<handle>.js,
  // resolving price + availability), disable every CTA so the visitor
  // can't submit the form against a stale variant.
  function setLoading(root, loading) {
    var btns = [].concat(
      Array.from(root.querySelectorAll('[data-pdp2-cta]')),
      Array.from(document.querySelectorAll('[data-pdp2-sticky-cta]'))
    );
    btns.forEach(function (b) {
      if (loading) {
        b.dataset.pdp2WasDisabled = b.disabled ? '1' : '0';
        b.disabled = true;
        b.classList.add('pdp2-cta--loading');
      } else {
        b.classList.remove('pdp2-cta--loading');
        // Only restore enabled state if it wasn't already disabled before.
        if (b.dataset.pdp2WasDisabled === '0') b.disabled = false;
        delete b.dataset.pdp2WasDisabled;
      }
    });
  }

  // Show/hide ATC vs Notify Me based on the matched variant's availability.
  // `variant.available` from /products/<handle>.js already accounts for the
  // "continue selling when out of stock" inventory_policy, so a product with
  // that flag set stays purchasable and Notify Me never appears.
  // Toggles ALL three locations: main action row, desktop sticky header,
  // and mobile sticky bottom bar — they share the data-pdp2-cta /
  // data-pdp2-sticky-cta / data-pdp2-sticky-notify attributes.
  function toggleAvailability(root, variant) {
    var available = !!variant.available;
    var hide = function (el, hidden) { if (el) el.classList.toggle('pdp2-cta--hidden', hidden); };
    var disable = function (el, disabled) { if (el) el.disabled = disabled; };

    // Main action row
    var atc = root.querySelector('[data-pdp2-cta]');
    var notify = root.querySelector('[data-pdp2-notify-btn]');
    hide(atc, !available);
    hide(notify, available);
    disable(atc, !available);

    // Sticky bars — desktop header + mobile bottom share these selectors,
    // querySelectorAll covers both occurrences.
    document.querySelectorAll('[data-pdp2-sticky-cta]').forEach(function (b) {
      hide(b, !available);
      disable(b, !available);
    });
    document.querySelectorAll('[data-pdp2-sticky-notify]').forEach(function (b) {
      hide(b, available);
    });

    // Refresh the modal's body with the active variant title.
    var body = root.querySelector('[data-pdp2-notify-body]');
    if (body) {
      var t = (root.querySelector('.pdp2-title') || {}).textContent || '';
      body.value = 'Notify me when ' + t.trim() + ' (variant: ' + variant.title + ') is back in stock.';
    }
  }

  // ══════════════════════════════════════════════════════════════════
  // Tabs
  // ══════════════════════════════════════════════════════════════════
  function wireTabs(root) {
    var bar = root.querySelector('.pdp2-tab-bar');
    if (!bar) return;
    bar.querySelectorAll('[data-pdp2-tab]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-pdp2-tab');
        bar.querySelectorAll('[data-pdp2-tab]').forEach(function (b) { b.classList.remove('on'); });
        btn.classList.add('on');
        root.querySelectorAll('[data-pdp2-panel]').forEach(function (p) {
          p.classList.toggle('on', p.getAttribute('data-pdp2-panel') === id);
        });
      });
    });
  }

  // ══════════════════════════════════════════════════════════════════
  // Prepare method switcher
  // ══════════════════════════════════════════════════════════════════
  function wirePrepMethods(root) {
    var prep = root.querySelector('.pdp2-prep');
    if (!prep) return;
    var titleEl = prep.querySelector('[data-prep-title]');
    var stepLists = prep.querySelectorAll('.pdp2-prep__step-list');
    prep.querySelectorAll('.pdp2-prep__method-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = btn.getAttribute('data-prep-method');
        prep.querySelectorAll('.pdp2-prep__method-btn').forEach(function (b) { b.classList.remove('on'); });
        btn.classList.add('on');
        if (titleEl) {
          var name = btn.querySelector('.pdp2-prep__method-name');
          if (name) titleEl.textContent = name.textContent.trim();
        }
        stepLists.forEach(function (sl) {
          var slIdx = sl.getAttribute('data-prep-steps-idx');
          sl.classList.toggle('on', slIdx === idx);
        });
      });
    });
  }

  // ══════════════════════════════════════════════════════════════════
  // Shipping accordion
  // ══════════════════════════════════════════════════════════════════
  function wireShipAccordion(root) {
    root.querySelectorAll('[data-pdp2-ship-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var region = btn.closest('.pdp2-ship__region');
        if (!region) return;
        var body = region.querySelector('.pdp2-ship__body');
        var icon = region.querySelector('.pdp2-ship__icon');
        var isOpen = region.classList.contains('open');
        region.classList.toggle('open', !isOpen);
        if (body) body.style.display = isOpen ? 'none' : 'block';
        if (icon) icon.textContent = isOpen ? '+' : '−';
      });
    });
  }

  // ══════════════════════════════════════════════════════════════════
  // Key Info accordion
  // ══════════════════════════════════════════════════════════════════
  function wireKeyInfo(root) {
    root.querySelectorAll('[data-pdp2-keyinfo-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var box = btn.closest('.pdp2-keyinfo');
        if (!box) return;
        var open = box.classList.toggle('open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  // ══════════════════════════════════════════════════════════════════
  // SEO accordion
  // ══════════════════════════════════════════════════════════════════
  function wireSEO(root) {
    root.querySelectorAll('[data-pdp2-seo-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('.pdp2-seo');
        if (!wrap) return;
        var open = wrap.classList.toggle('open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  // ══════════════════════════════════════════════════════════════════
  // Gallery
  // ══════════════════════════════════════════════════════════════════
  function wireGallery(root) {
    var main = root.querySelector('[data-pdp2-main-image]');
    if (!main) return;
    var thumbs = root.querySelectorAll('[data-pdp2-thumb]');
    var dots = root.querySelectorAll('[data-pdp2-dot]');
    var prevBtn = root.querySelector('[data-pdp2-prev]');
    var nextBtn = root.querySelector('[data-pdp2-next]');
    var total = thumbs.length || 1;
    var current = 0;

    // Sync `current` to whichever thumb is currently marked .on. The filter
    // function (filterGalleryForVariant) mutates the .on class directly when
    // a variant changes, so without this sync prev/next would advance from
    // a stale index.
    function syncCurrentFromOnClass() {
      for (var i = 0; i < thumbs.length; i++) {
        if (thumbs[i].classList.contains('on')) { current = i; return; }
      }
    }

    // Find the next visible thumb in the requested direction, wrapping
    // around. Hidden thumbs (filtered out by variant) are skipped so the
    // arrows / swipe only cycle through the active variant's images.
    function nextVisibleFrom(start, dir) {
      if (!total) return start;
      var i = start;
      for (var k = 0; k < total; k++) {
        i = (i + dir + total) % total;
        if (thumbs[i] && thumbs[i].style.display !== 'none') return i;
      }
      return start;
    }

    function setIdx(i) {
      if (i < 0) i = total - 1;
      if (i >= total) i = 0;
      // Defensive: if caller asked for a hidden thumb, jump to the next
      // visible one. This shouldn't normally happen because prev/next/swipe
      // already filter to visible, but thumb/dot clicks may target stale state.
      if (thumbs[i] && thumbs[i].style.display === 'none') {
        i = nextVisibleFrom(i, 1);
      }
      if (i === current) return;
      var prevIdx = current;
      current = i;
      thumbs.forEach(function (t) { t.classList.toggle('on', t.getAttribute('data-pdp2-thumb') === String(i)); });
      dots.forEach(function (d) { d.classList.toggle('on', d.getAttribute('data-pdp2-dot') === String(i)); });
      var src = thumbs[i] && thumbs[i].getAttribute('data-src');
      if (!src || !main || main.src === src) return;

      // Smooth crossfade: preload new image, fade the visible one to 0,
      // swap src, then fade back to 1 on next frame. The transition is
      // defined in CSS (.pdp2-img-main img { transition: opacity ... }).
      var direction = (i > prevIdx || (prevIdx === total - 1 && i === 0)) ? 1 : -1;
      main.style.willChange = 'opacity, transform';
      var pre = new Image();
      var doSwap = function () {
        main.style.transition = 'opacity 180ms cubic-bezier(.2,.7,.2,1), transform 260ms cubic-bezier(.2,.7,.2,1)';
        main.style.opacity = '0';
        main.style.transform = 'translateX(' + (direction * -18) + 'px)';
        setTimeout(function () {
          // Initial image uses srcset/sizes for LCP; subsequent swaps
          // bind to a single `src` so we need to strip srcset or the
          // browser keeps resolving to the original image's sources.
          if (main.hasAttribute('srcset')) {
            main.removeAttribute('srcset');
            main.removeAttribute('sizes');
          }
          main.src = src;
          main.style.transform = 'translateX(' + (direction * 18) + 'px)';
          requestAnimationFrame(function () {
            main.style.opacity = '1';
            main.style.transform = 'translateX(0)';
          });
          setTimeout(function () { main.style.willChange = ''; }, 300);
        }, 160);
      };
      // Preload (if already cached this fires sync via complete check)
      if (pre.complete || pre.decode) {
        pre.src = src;
        if (pre.decode) {
          pre.decode().then(doSwap).catch(doSwap);
        } else {
          pre.onload = doSwap;
          pre.onerror = doSwap;
        }
      } else {
        pre.onload = doSwap;
        pre.onerror = doSwap;
        pre.src = src;
      }
    }

    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { setIdx(i); }); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { setIdx(i); }); });
    if (prevBtn) prevBtn.addEventListener('click', function () { syncCurrentFromOnClass(); setIdx(nextVisibleFrom(current, -1)); });
    if (nextBtn) nextBtn.addEventListener('click', function () { syncCurrentFromOnClass(); setIdx(nextVisibleFrom(current, +1)); });

    // Touch swipe — left/right on the main image area changes slide.
    // Only acts on horizontal-dominant gestures so vertical page scroll
    // still works when the user drags up/down from the image.
    var swipeHost = root.querySelector('.pdp2-img-main-wrap') || main.parentNode;
    if (swipeHost && total > 1) {
      var startX = 0, startY = 0, tracking = false, didMove = false;
      var THRESHOLD = 40;          // px horizontal before we commit
      var V_BIAS = 1.3;            // horizontal must exceed vertical by this
      swipeHost.addEventListener('touchstart', function (e) {
        if (!e.touches || e.touches.length !== 1) return;
        tracking = true; didMove = false;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      swipeHost.addEventListener('touchmove', function (e) {
        if (!tracking || !e.touches || e.touches.length !== 1) return;
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * V_BIAS) didMove = true;
      }, { passive: true });
      swipeHost.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        var touch = (e.changedTouches && e.changedTouches[0]) || null;
        if (!touch || !didMove) return;
        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;
        if (Math.abs(dx) < THRESHOLD) return;
        if (Math.abs(dx) <= Math.abs(dy) * V_BIAS) return;   // dominantly vertical → ignore
        syncCurrentFromOnClass();
        setIdx(nextVisibleFrom(current, dx < 0 ? +1 : -1));
      }, { passive: true });
      // Prevent ghost-click on the image after a swipe
      swipeHost.addEventListener('click', function (e) {
        if (didMove) { e.preventDefault(); e.stopPropagation(); didMove = false; }
      }, true);
    }
  }

  // ══════════════════════════════════════════════════════════════════
  // Sticky header offset
  // ══════════════════════════════════════════════════════════════════
  function wireStickyHeaderOffset(root) {
    var bar = root.querySelector('[data-pdp2-sticky]');
    if (!bar) return;
    function measure() {
      var header = document.querySelector('.header-wrapper, sticky-header, .shopify-section-group-header-group, header');
      var offset = 0;
      if (header) {
        var rect = header.getBoundingClientRect();
        offset = Math.max(0, rect.bottom);
      }
      bar.style.setProperty('--pdp2-header-offset', offset + 'px');
    }
    measure();
    var rafPending = false;
    function onScroll() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        measure();
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  // ══════════════════════════════════════════════════════════════════
  // Sticky ATC → submits the main form
  // ══════════════════════════════════════════════════════════════════
  function wireStickyATC(root, state) {
    var btns = root.querySelectorAll('[data-pdp2-sticky-cta]');
    if (!btns.length) return;
    var form = root.querySelector('#pdp2-form');
    var resetTimer;

    var ctrls = Array.prototype.map.call(btns, function (btn) {
      return { btn: btn, originalLabel: btn.textContent };
    });

    function setLoading(on) {
      ctrls.forEach(function (c) {
        var btn = c.btn;
        if (on) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
          btn.classList.add('loading');
          btn.textContent = '';
          var spinner = document.createElement('span');
          spinner.className = 'pdp2-sticky__spinner';
          spinner.setAttribute('aria-hidden', 'true');
          var label = document.createElement('span');
          label.className = 'pdp2-sticky__cta-label';
          label.textContent = 'Adding…';
          btn.appendChild(spinner);
          btn.appendChild(label);
        } else {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          btn.classList.remove('loading');
          btn.textContent = c.originalLabel;
        }
      });
    }

    function reset() {
      clearTimeout(resetTimer);
      setLoading(false);
    }

    ctrls.forEach(function (c) {
      c.btn.addEventListener('click', function () {
        if (c.btn.disabled) return;
        setLoading(true);
        // Fallback — product-form eventually fires its own events, but if
        // something swallows them the button must still un-stick. 2.5s is
        // enough for a normal /cart/add.js + sections round-trip; anything
        // slower is probably already errored out.
        resetTimer = setTimeout(reset, 2500);
        if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    });

    // Dawn's product-form publishes these via pubsub.js. `subscribe` is
    // a global function declared in pubsub.js (let/const at top level
    // of a classic script is NOT on window, but function declarations
    // are). Event keys come from constants.js PUB_SUB_EVENTS and are
    // literal strings — safe to use directly.
    try {
      if (typeof subscribe === 'function') {
        subscribe('cart-update', reset);
        subscribe('cart-error', reset);
      }
    } catch (_) {}
    document.addEventListener('cart:item-added', reset);
    document.addEventListener('cart-notification:open', reset);
  }

  // ══════════════════════════════════════════════════════════════════
  // "Be the first" / "Write a review" → open Judge.me form
  // ══════════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════════
  // Cross-sell arrow buttons — scroll the horizontal carousel one
  // card-width at a time. Swipe/touch-scroll still works natively.
  // ══════════════════════════════════════════════════════════════════
  // Cross-sell "Shop Now" → "Add to Cart" text swap + direct-add click.
  // The desktop link normally navigates to the PDP; we rewrite the label
  // and on click POST straight to /cart/add.js with the card's first
  // available variant, then open the cart drawer — no PDP navigation and
  // no quick-add modal for the multi-variant case (user wants the first
  // variant added directly). Scoped to .pdp2-cs only so the same
  // card-product snippet still reads "Shop Now" and navigates on
  // collection pages etc. Re-runs after AJAX rerenders swap the DOM.
  function resolveCardVariantId(card) {
    var hidden = card.querySelector('input[name="id"].product-variant-id, input.product-variant-id[name="id"]');
    if (hidden && hidden.value) return hidden.value;
    var dv = card.querySelector('[data-variant-id]');
    if (dv && dv.getAttribute('data-variant-id')) return dv.getAttribute('data-variant-id');
    var selectedOpt = card.querySelector('select[name="id"] option:checked, select[name="id"] option[selected]');
    if (selectedOpt && selectedOpt.value) return selectedOpt.value;
    return null;
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (_) {}
    }
  }

  function directAddToCart(btn, card) {
    // Loading state on the clicked button
    var originalText = btn.textContent;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Adding…';
    btn.style.pointerEvents = 'none';
    function restore() {
      btn.removeAttribute('aria-busy');
      btn.textContent = originalText;
      btn.style.pointerEvents = '';
    }

    // Resolve the sections cart-drawer expects to re-render. Sending them
    // in /cart/add.js lets one response atomically refresh the drawer
    // body AND the header cart-icon-bubble. The previous implementation
    // skipped this — it called drawer.onCartUpdate() (a method that
    // doesn't exist on cart-drawer; it's on cart-items), so the drawer
    // opened with stale HTML and the header count stayed stuck.
    var drawer = document.querySelector('cart-drawer');
    var sectionIds = ['cart-drawer', 'cart-icon-bubble'];
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try { sectionIds = drawer.getSectionsToRender().map(function (s) { return s.id; }); } catch (_) {}
    }

    function postAdd(id) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(id, 10), quantity: 1 }],
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || 'Add to cart failed'); });
        return r.json();
      });
    }

    var id = resolveCardVariantId(card);
    var promise;
    if (id) {
      promise = postAdd(id);
    } else {
      // Fallback — pull the handle from the card link and fetch product JSON
      // to find the first available variant.
      var link = card.querySelector('a[href*="/products/"]');
      var href = link && link.getAttribute('href');
      if (!href) { restore(); return; }
      var handle = href.split('/products/')[1].split('?')[0].split('/')[0];
      promise = fetch('/products/' + handle + '.js').then(function (r) { return r.json(); })
        .then(function (p) {
          var v = (p.variants || []).find(function (x) { return x.available; }) || (p.variants || [])[0];
          if (!v) throw new Error('No variant');
          return postAdd(v.id);
        });
    }

    promise.then(function (parsed) {
      // Atomic: cart-drawer.renderContents iterates its sectionsToRender
      // and replaces #CartDrawer + cart-icon-bubble in one shot from
      // parsed.sections. Same pattern product-form.js uses.
      if (drawer && typeof drawer.renderContents === 'function' && parsed && parsed.sections) {
        try { drawer.renderContents(parsed); } catch (err) { console.warn('[pdp2-cs] renderContents', err); }
      } else {
        openCartDrawer();
      }
      // Drop is-empty if drawer was opened with an empty cart and the
      // user added from the cross-sell. renderContents replaces inner
      // HTML but doesn't toggle this class; CSS uses it to hide cart
      // contents.
      if (drawer && drawer.classList.contains('is-empty')) {
        drawer.classList.remove('is-empty');
      }
      // Refresh shipping notices (free-shipping bar etc).
      if (window.ShopUtils && typeof window.ShopUtils.updateShippingNotices === 'function') {
        window.ShopUtils.updateShippingNotices();
      }
      // Notify any listeners (loyalty / analytics).
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: parsed }));
      if (window.PUB_SUB_EVENTS && window.publish) {
        try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pdp2-cs', cartData: parsed }); } catch (_) {}
      }
      restore();
    }).catch(function (err) {
      console.error('[pdp2] add-to-cart failed:', err);
      restore();
    });
  }

  function relabelCsShopNow(root) {
    var scope = root.querySelector('.pdp2-cs');
    if (!scope) return;
    scope.querySelectorAll('a.button.btn-cream, .pdp2-cs__slide .button--full-width').forEach(function (btn) {
      if (btn.dataset.pdp2Relabeled === '1') return;
      var txt = (btn.textContent || '').trim();
      if (!/^shop now$/i.test(txt)) return;
      btn.textContent = 'Add to Cart';
      btn.dataset.pdp2Relabeled = '1';
      btn.addEventListener('click', function (e) {
        var card = btn.closest('.card-wrapper, .wishlist-grid, li, .pdp2-cs__slide');
        if (!card) return;
        e.preventDefault();
        directAddToCart(btn, card);
      });
    });
  }

  function wireCsCarousel(root) {
    var scroller = root.querySelector('[data-pdp2-cs-scroller]');
    if (!scroller) return;
    relabelCsShopNow(root);
    // Observe the scroller for AJAX rerenders and relabel new cards.
    var mo = new MutationObserver(function () { relabelCsShopNow(root); });
    mo.observe(scroller, { childList: true, subtree: true });
    var prev = root.querySelector('[data-pdp2-cs-prev]');
    var next = root.querySelector('[data-pdp2-cs-next]');
    var step = function () {
      var slide = scroller.querySelector('.pdp2-cs__slide');
      if (!slide) return 200;
      var styles = window.getComputedStyle(scroller);
      var gap = parseFloat(styles.columnGap || styles.gap || '12') || 12;
      return slide.getBoundingClientRect().width + gap;
    };
    var refresh = function () {
      if (prev) prev.disabled = scroller.scrollLeft <= 2;
      if (next) next.disabled = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
    };
    if (prev) prev.addEventListener('click', function (e) {
      e.preventDefault();
      scroller.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function (e) {
      e.preventDefault();
      scroller.scrollBy({ left:  step(), behavior: 'smooth' });
    });
    scroller.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    refresh();
  }

  // ══════════════════════════════════════════════════════════════════
  // "You might also like" — ports the old sections/related-products.liquid
  // logic into the info-column scroller.
  //   Stage 1 primary: <product-recommendations> Dawn AJAX (ML recs).
  //   Stage 2 fallback: tag-match handles + 72h cookie rotation, each
  //     fetched via /products/{handle}?view=card-product (returns a
  //     pre-rendered card with .wishlist-grid wrapper).
  // After content lands we normalise grid children to .pdp2-cs__slide
  // so the carousel + arrow logic can work over a consistent markup.
  // ══════════════════════════════════════════════════════════════════
  function wireCsRecommendations(root) {
    // Re-query every call — Dawn's product-info morph can replace the
    // <product-recommendations> element entirely on variant change.
    function currentHost() {
      return root.querySelector('[data-pdp2-cs-scroller]');
    }
    var host = currentHost();
    if (!host) return;

    var normaliseCards = function () {
      var h = currentHost();
      if (!h) return;

      // Drop Shopify's section heading — we own the header row.
      h.querySelectorAll(':scope > h2, :scope > .related-products__heading, :scope > ul > h2').forEach(function (el) { el.remove(); });

      // templates/product.card-product.liquid wraps each .wishlist-grid
      // with a `.wishlist-btn-js.wishlist-remove` X button intended for
      // the Wishlist page. In cross-sell context it appears as a
      // duplicate heart next to the real "add to wishlist" button.
      h.querySelectorAll('.wishlist-btn-js.wishlist-remove').forEach(function (el) { el.remove(); });

      // Keep the theme's <modal-opener> + <quick-add-modal> so the
      // Quick-Add modal opens on click (same UX as home-page cards).
      // quick-add.js lazy-loads pdp-v2 assets and dispatches
      // shopify:section:load after injecting content, so the modal
      // re-initialises correctly in this AJAX-loaded context.
      // ensureQuickAddButtons() below only fires for cards that arrive
      // WITHOUT a modal-opener (fallback path).

      // Each .grid__item contains one card; unwrap into .pdp2-cs__slide
      // so we don't have an orphan <li> outside a <ul>.
      h.querySelectorAll('.grid__item').forEach(function (li) {
        if (li.closest('.pdp2-cs__slide')) return;
        var slide = document.createElement('div');
        slide.className = 'pdp2-cs__slide';
        while (li.firstChild) slide.appendChild(li.firstChild);
        li.parentNode.insertBefore(slide, li);
        li.remove();
      });

      // Flatten any leftover <ul class="grid"> — move slides up to host.
      h.querySelectorAll(':scope > ul, :scope > .grid').forEach(function (wrapper) {
        while (wrapper.firstChild) h.appendChild(wrapper.firstChild);
        wrapper.remove();
      });

      // Fallback path: cards may arrive as top-level .wishlist-grid or
      // .card-wrapper children (not wrapped in .grid__item). Wrap each
      // into a .pdp2-cs__slide so sizing + scoped CSS applies.
      h.querySelectorAll(':scope > .wishlist-grid, :scope > .card-wrapper').forEach(function (card) {
        var slide = document.createElement('div');
        slide.className = 'pdp2-cs__slide';
        card.parentNode.insertBefore(slide, card);
        slide.appendChild(card);
      });

      ensureVendorLabels(h);
      // Inject a per-card mobile "Add to cart" button (visibility handled
      // in CSS @media max-width: 899px). AJAX-adds the first available
      // variant via /cart/add.js + Dawn's cart-notification/drawer
      // renderContents pipeline — same pathway product-form uses.
      h.querySelectorAll('.pdp2-cs__slide').forEach(function (slide) {
        if (slide.querySelector('.pdp2-cs__mobile-atc')) return;
        var link = slide.querySelector('a[href*="/products/"]');
        var m = link ? (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/) : null;
        var handle = m ? m[1] : null;
        if (!handle) return;
        var content = slide.querySelector('.card__content') || slide.querySelector('.card__information');
        if (!content) return;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdp2-cs__mobile-atc';
        btn.setAttribute('data-handle', handle);
        btn.innerHTML = '<span class="pdp2-cs__mobile-atc-label">Add to cart</span>';
        content.appendChild(btn);
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (btn.classList.contains('loading')) return;
          btn.classList.add('loading');
          var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          var cartUi = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          var sectionIds = ['cart-notification', 'cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'];
          if (cartUi && typeof cartUi.getSectionsToRender === 'function') {
            try { sectionIds = cartUi.getSectionsToRender().map(function (s) { return s.id; }); } catch (_) {}
          }
          fetch(rootPath + 'products/' + handle + '.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (p) {
              var variants = (p && p.variants) || [];
              var first = variants.filter(function (v) { return v.available; })[0] || variants[0];
              if (!first) throw new Error('no variant');
              var fd = new FormData();
              fd.append('id', first.id);
              fd.append('quantity', 1);
              fd.append('sections', sectionIds.join(','));
              fd.append('sections_url', window.location.pathname);
              return fetch(rootPath + 'cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: fd,
              });
            })
            .then(function (r) { return r.json(); })
            .then(function (parsed) {
              btn.classList.remove('loading');
              btn.classList.add('added');
              setTimeout(function () { btn.classList.remove('added'); }, 1100);
              if (cartUi && typeof cartUi.renderContents === 'function' && parsed && parsed.sections) {
                try { cartUi.renderContents(parsed); } catch (err) { console.warn('[pdp2-cs] renderContents', err); }
              }
              document.dispatchEvent(new CustomEvent('cart:item-added', { detail: parsed }));
              if (window.PUB_SUB_EVENTS && window.publish) {
                try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pdp2-cs-mobile', cartData: parsed }); } catch (_) {}
              }
            })
            .catch(function (err) {
              btn.classList.remove('loading');
              btn.classList.add('error');
              setTimeout(function () { btn.classList.remove('error'); }, 1400);
              console.warn('[pdp2-cs-mobile-atc] failed', err);
            });
        });
      });

      // Strip "From " prefix on multi-variant products and collapse the
      // "250 g / 1 Kg" variant range in the size line to only the first
      // size — keeps the cross-sell card pricing line clean.
      h.querySelectorAll('.pdp2-cs__slide .price').forEach(function (price) {
        price.querySelectorAll('*').forEach(function (el) {
          el.childNodes.forEach(function (n) {
            if (n.nodeType === 3 && /\bFrom\s*/i.test(n.nodeValue)) {
              n.nodeValue = n.nodeValue.replace(/\bFrom\s*/i, '');
            }
          });
        });
      });
      h.querySelectorAll('.pdp2-cs__slide .product-size').forEach(function (size) {
        var t = (size.textContent || '').trim();
        if (t.indexOf('/') > -1) {
          size.textContent = t.split('/')[0].trim();
        }
      });
      ensureQuickAddButtons(h);
      wireCsCarousel(root); // re-bind arrows on the fresh DOM

      // Dawn's <modal-opener> + <quick-add-modal> ship with their own
      // connected callbacks that bind open/close handlers. When they
      // arrive via AJAX (innerHTML assignment) their connectedCallbacks
      // already fire; but the inner modal needs section-scoped JS (like
      // pdp-v2-core for the full product-info). Dispatching section:load
      // triggers any listeners that re-wire behaviour.
      //
      // Debounced: every mutation inside the cards (color swatches,
      // wishlist toggles, modal-opener boot) re-enters normaliseCards via
      // the MutationObserver. Without this guard the dispatch fires
      // thousands of times during recommendations render.
      try {
        if (!window.__pdp2RelatedSectionLoadFired) {
          window.__pdp2RelatedSectionLoadFired = true;
          document.dispatchEvent(new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: 'related-products' },
          }));
          // Re-arm after 1 second so a real fresh fetch (variant change)
          // can dispatch once more.
          setTimeout(function () { window.__pdp2RelatedSectionLoadFired = false; }, 1000);
        }
      } catch (_) {}
    };

    // Inject a quick-add button on cards where the theme didn't render
    // one (e.g. quick_add setting was 'none' for that section). Clicks
    // POST to /cart/add.js with the first available variant.
    function ensureQuickAddButtons(h) {
      if (!h) return;
      h.querySelectorAll('.pdp2-cs__slide').forEach(function (slide) {
        if (slide.querySelector('modal-opener, .quick-add-btn, .quick-add__submit, .pdp2-cs__quickadd')) return;
        if (slide.getAttribute('data-pdp2-quickadd-injected')) return;
        var imageWrap = slide.querySelector('.wrapper-image-card');
        if (!imageWrap) return;
        var card = slide.querySelector('.wishlist-grid, [data-product-handle]');
        var handle = card && card.getAttribute('data-product-handle');
        if (!handle) {
          var link = slide.querySelector('a[href*="/products/"]');
          if (link) {
            var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
            if (m) handle = m[1];
          }
        }
        if (!handle) return;
        slide.setAttribute('data-pdp2-quickadd-injected', '1');
        if (getComputedStyle(imageWrap).position === 'static') {
          imageWrap.style.position = 'relative';
        }
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'quick-add-btn quick-add__submit pdp2-cs__quickadd';
        btn.setAttribute('aria-label', 'Quick add');
        btn.setAttribute('aria-haspopup', 'dialog');
        btn.setAttribute('data-handle', handle);
        // data-product-url is what quick-add-modal.show() reads to fetch
        // the product page's <product-info> and inject it into the modal.
        var productUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/')
                         + 'products/' + handle;
        btn.setAttribute('data-product-url', productUrl);
        // A loading spinner placeholder the quick-add modal toggles on click.
        var spinner = document.createElement('span');
        spinner.className = 'loading__spinner hidden';
        spinner.style.display = 'none';
        btn.appendChild(spinner);
        imageWrap.appendChild(btn);
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          var modal = document.getElementById('PDP2CsQuickAdd');
          if (modal && typeof modal.show === 'function') {
            // The QuickAddModal constructor captures modalContent via
            // this.querySelector('[id^="QuickAddInfo-"]') — if it ran
            // before our child <div> was parsed, modalContent is null
            // and show() crashes inside HTMLUpdateUtility.setInnerHTML.
            // Re-bind at click time to guarantee it points at the node.
            if (!modal.modalContent) {
              modal.modalContent = modal.querySelector('[id^="QuickAddInfo-"]')
                                 || modal.querySelector('.quick-add-modal__content-info');
            }
            modal.show(btn);
          } else {
            // Modal custom-element not defined or missing — navigate to
            // the product page as a graceful fallback.
            window.location.href = productUrl;
          }
        });
      });
    }

    // If a card's markup doesn't include a vendor element (can happen
    // when Dawn's recommendations response strips it or show_vendor
    // defaults to false), fetch /products/{handle}.js and inject an
    // <a class="vendor"> before the title.
    function ensureVendorLabels(h) {
      if (!h) return;
      h.querySelectorAll('.pdp2-cs__slide .card__information').forEach(function (info) {
        if (info.querySelector('.vendor, .caption-with-letter-spacing, .card__vendor')) return;
        if (info.getAttribute('data-pdp2-vendor-requested')) return;
        // Read handle from each card's own product link first — closest()
        // can otherwise walk up to <product-info> (which carries the
        // current page's data-product-handle) and stamp every card with
        // the page's vendor instead of the card's own vendor.
        var handle = '';
        var link = info.querySelector('a[href*="/products/"]') ||
                   (info.closest('.pdp2-cs__slide, .grid__item, .card-wrapper, .wishlist-grid') || info)
                     .querySelector('a[href*="/products/"]');
        if (link) {
          var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
          if (m) handle = m[1];
        }
        if (!handle) {
          var card = info.closest('.pdp2-cs__slide, .grid__item, .card-wrapper, .wishlist-grid');
          if (card) handle = card.getAttribute('data-product-handle') || '';
        }
        if (!handle) return;
        info.setAttribute('data-pdp2-vendor-requested', '1');
        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
          .then(function (p) {
            if (!p || !p.vendor) return;
            if (info.querySelector('.vendor, .caption-with-letter-spacing, .card__vendor')) return;
            var v = document.createElement('a');
            v.className = 'vendor';
            v.href = '/collections/' + String(p.vendor).toLowerCase().replace(/[^a-z0-9]+/g, '-');
            v.textContent = p.vendor;
            info.insertBefore(v, info.firstChild);
          })
          .catch(function () {});
      });
    }

    // Watch the entire product-info subtree. Dawn's morph can replace
    // <product-recommendations> and its children wholesale on variant
    // change — a child-only observer on the old host is detached and
    // never fires. A debounced root-level observer catches every case.
    var rafId = null;
    var schedule = function () {
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        var h = currentHost();
        if (!h) return;
        // Only work if raw ajax markup is present OR no slides yet
        var hasRaw = h.querySelector('.grid__item, :scope > ul, :scope > .grid, :scope > .wishlist-grid, :scope > .card-wrapper, :scope > h2, :scope > .related-products__heading');
        var hasSlides = h.querySelector('.pdp2-cs__slide');
        if (hasRaw || !hasSlides) normaliseCards();
      });
    };
    var observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });

    var runFallback = function () {
      var raw = host.getAttribute('data-fallback-handles') || '';
      var allHandles = raw.split(',').map(function (h) { return h.trim(); }).filter(Boolean);
      if (!allHandles.length) return;

      var productId = host.getAttribute('data-product-id') || host.dataset.productId || '';
      var cookieName = 'reco_rot_' + productId;
      var cookie = document.cookie.split('; ').filter(function (c) { return c.indexOf(cookieName + '=') === 0; })[0];
      var selected;
      if (cookie) {
        selected = decodeURIComponent(cookie.split('=')[1]).split(',').filter(Boolean);
      } else {
        var shuffled = allHandles.slice();
        for (var i = shuffled.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var t = shuffled[i]; shuffled[i] = shuffled[j]; shuffled[j] = t;
        }
        selected = shuffled.slice(0, 4);
        if (selected.length) {
          var d = new Date();
          d.setTime(d.getTime() + 72 * 60 * 60 * 1000);
          document.cookie = cookieName + '=' + encodeURIComponent(selected.join(',')) +
            ';expires=' + d.toUTCString() + ';path=/;samesite=lax';
        }
      }

      host.innerHTML = '';
      var root_path = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      selected.forEach(function (handle) {
        var slide = document.createElement('div');
        slide.className = 'pdp2-cs__slide';
        host.appendChild(slide);
        fetch(root_path + 'products/' + handle + '/?view=card-product')
          .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var card = parsed.querySelector('.wishlist-grid, .card-wrapper, .card');
            if (card) slide.appendChild(card);
          })
          .catch(function (e) { console.warn('[pdp2-cs] fallback fetch failed for', handle, e); });
      });
      wireCsCarousel(root);
    };

    // Watch the <product-recommendations> element for innerHTML swaps.
    // Dawn's global.js fetches lazily on IntersectionObserver trigger, so
    // the swap can happen immediately on load or minutes later on slow
    // scroll. A fixed poll window misses the long-tail case — use a
    // MutationObserver that lives as long as the page does.
    var normalisedOnce = false;
    function maybeNormalise() {
      if (normalisedOnce) return;
      if (host.querySelector('.grid__item, .pdp2-cs__slide')) {
        normalisedOnce = true;
        normaliseCards();
      }
    }
    // Run once synchronously in case content is already present
    maybeNormalise();
    if (!normalisedOnce && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        maybeNormalise();
        if (normalisedOnce) mo.disconnect();
      });
      mo.observe(host, { childList: true, subtree: true });
    }
    // Fallback path — if after 8s the primary still hasn't landed AND
    // the host shows the loaded class but is empty (empty ML result),
    // swap in our tag-matched fallback instead.
    setTimeout(function () {
      if (normalisedOnce) return;
      if (host.classList.contains('product-recommendations--loaded') &&
          !host.querySelector('.grid__item, .pdp2-cs__slide')) {
        runFallback();
      }
    }, 8000);
  }

  // ══════════════════════════════════════════════════════════════════
  // Cross-sell icon tooltips — render at document.body level with
  // position: fixed so they escape the scroller's overflow clipping.
  // Original in-card tooltip is suppressed via CSS; we show a single
  // shared floating bubble positioned above the hovered icon.
  // ══════════════════════════════════════════════════════════════════
  function wireCsTooltips(root) {
    var host = root.querySelector('[data-pdp2-cs-scroller]');
    if (!host) return;
    if (wireCsTooltips._bound && wireCsTooltips._bound === host) return;
    wireCsTooltips._bound = host;

    var tip = document.getElementById('pdp2-cs-float-tip');
    if (!tip) {
      tip = document.createElement('div');
      tip.id = 'pdp2-cs-float-tip';
      tip.className = 'pdp2-cs-float-tip';
      document.body.appendChild(tip);
    }

    function showTip(target) {
      var src = target.querySelector('.tooltiptext, .text-with-icon__label_mobile, .text-with-icon__label');
      var text = '';
      if (src) text = src.textContent.trim();
      if (!text) text = (target.getAttribute('data-tooltip') || target.getAttribute('title') || '').trim();
      if (!text) {
        var img = target.querySelector('img[alt]');
        if (img) text = img.getAttribute('alt').trim();
      }
      if (!text) return;
      tip.textContent = text;
      tip.classList.add('show');
      // Measure after showing (layout pass)
      var rect = target.getBoundingClientRect();
      var tipRect = tip.getBoundingClientRect();
      var vpW = document.documentElement.clientWidth;
      var x = rect.left + rect.width / 2 - tipRect.width / 2;
      x = Math.max(8, Math.min(x, vpW - tipRect.width - 8));
      var y = rect.top - tipRect.height - 8;
      if (y < 4) y = rect.bottom + 8; // flip below if no room above
      tip.style.left = x + 'px';
      tip.style.top = y + 'px';
    }

    function hideTip() { tip.classList.remove('show'); }

    host.addEventListener('mouseover', function (e) {
      var t = e.target.closest && e.target.closest('.tooltip');
      if (t && host.contains(t)) showTip(t);
    });
    host.addEventListener('mouseout', function (e) {
      var t = e.target.closest && e.target.closest('.tooltip');
      if (t) hideTip();
    });
    host.addEventListener('scroll', hideTip, { passive: true });
    window.addEventListener('scroll', hideTip, { passive: true });
  }

  function wireReviewsHook(root) {
    function openReviewsTab() {
      // Scroll the user straight to the bottom reviews block (above the
      // footer). That block renders its own always-visible "Write a
      // review" button via Judge.me, so no DOM-racing with the modal
      // form is needed — the user just sees the button and clicks it.
      var block = document.querySelector('.pdp2-reviews-block');
      if (block) {
        block.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // Fallback: open the reviews tab + scroll to it (no review block
      // exists on this page for whatever reason).
      var tabBtn = root.querySelector('[data-pdp2-tab="reviews"]');
      if (tabBtn) tabBtn.click();
      var panel = root.querySelector('.pdp2-panel[data-pdp2-panel="reviews"]');
      var scrollTarget = panel || tabBtn;
      if (scrollTarget) {
        requestAnimationFrame(function () {
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    document.addEventListener('click', function (e) {
      var link = e.target && e.target.closest && e.target.closest('[data-pdp2-review-link]');
      if (!link || !root.contains(link)) return;
      e.preventDefault();
      e.stopPropagation();
      openReviewsTab();
    }, true);
  }

  // ══════════════════════════════════════════════════════════════════
  // Chat widget lift
  // The Shopify Inbox chat widget is JS-driven: it sets `style.bottom`
  // on `.chat-app` (or `.chat-toggle`) inline, which beats CSS. On mobile
  // that puts the bubble on top of the floating sticky ATC. Watch the
  // body's class list for `scroll-start` and override the inline bottom
  // only while the sticky is visible and the viewport is phone-sized.
  // Restore the original value the moment either condition goes away.
  // ══════════════════════════════════════════════════════════════════
  function wireChatLift() {
    var LIFT_PX = 110;
    var mq = window.matchMedia('(max-width: 767px)');
    var originals = new WeakMap();

    function chatNodes() {
      // Shopify Inbox renders as <inbox-online-store-chat id="ShopifyChat">
      // at the top level; its button/panel live in its shadow DOM and are
      // unreachable from here. Moving the host lifts everything with it.
      return document.querySelectorAll('inbox-online-store-chat, #ShopifyChat');
    }

    // When the chat panel is open it takes most of the viewport height;
    // when only the bubble is visible the host is ~60px tall. Use height
    // as the open/closed signal so we only lift the closed bubble — the
    // open panel stays flush with the bottom of the screen.
    function isOpen(el) {
      return el.getBoundingClientRect().height > 200;
    }

    function lifted() {
      return document.body.classList.contains('scroll-start') && mq.matches;
    }

    function apply() {
      var on = lifted();
      chatNodes().forEach(function (el) {
        var open = isOpen(el);
        var shouldLift = on && !open;
        if (shouldLift) {
          if (!originals.has(el)) {
            originals.set(el, { bottom: el.style.bottom, transition: el.style.transition });
          }
          el.style.setProperty('bottom', LIFT_PX + 'px', 'important');
          el.style.setProperty('transition', 'bottom 320ms cubic-bezier(0.4, 0, 0.2, 1)', 'important');
        } else if (on && open) {
          // Panel is open on mobile while sticky is visible — pin it to
          // the bottom so the CSS lift rule doesn't leave a gap. Record
          // original so we can restore when it closes.
          if (!originals.has(el)) {
            originals.set(el, { bottom: el.style.bottom, transition: el.style.transition });
          }
          el.style.setProperty('bottom', '0px', 'important');
          el.style.setProperty('transition', 'none', 'important');
        } else if (originals.has(el)) {
          var prev = originals.get(el);
          el.style.bottom = prev.bottom || '';
          el.style.transition = prev.transition || '';
          originals.delete(el);
        }
      });
    }

    // Re-apply on body class change (scroll-start toggle)
    new MutationObserver(apply).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Re-apply when the chat widget mounts/unmounts or its own JS rewrites
    // inline style on `.chat-app` — observing subtree attribute changes so
    // we reassert our bottom value right after the widget re-sets it.
    new MutationObserver(function (records) {
      var shouldApply = false;
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'childList') {
          shouldApply = true; break;
        }
        if (r.type === 'attributes' && r.target && r.target.closest && r.target.closest('inbox-online-store-chat')) {
          shouldApply = true; break;
        }
      }
      if (shouldApply) apply();
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);

    // Opening/closing the chat panel resizes the host element without
    // changing any observable attribute on it — watch its box instead.
    var ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(apply) : null;
    var observed = new WeakSet();
    function observeHosts() {
      if (!ro) return;
      chatNodes().forEach(function (el) {
        if (!observed.has(el)) { ro.observe(el); observed.add(el); }
      });
    }
    observeHosts();
    new MutationObserver(observeHosts).observe(document.documentElement, {
      childList: true, subtree: true,
    });

    apply();
  }

  // ══════════════════════════════════════════════════════════════════
  // Localize the "Free shipping over X" threshold to the customer's
  // checkout currency using Shopify's own FX rate (window.Shopify.currency.
  // rate) — the same rate applied at checkout, no third-party guess.
  //
  // Server renders the threshold in its source currency (AED for UAE,
  // USD for ROW). If the customer's active currency differs AND the rate
  // is available, we convert and replace the inline span text.
  // UAE/AED customers always stay on AED — never converted.
  // ══════════════════════════════════════════════════════════════════
  function localizeShippingThreshold() {
    var nodes = document.querySelectorAll('[data-pdp2-ship-threshold]');
    if (!nodes.length) return;
    var s = window.Shopify && window.Shopify.currency;
    if (!s || !s.active) return;
    var active = s.active;
    var rate = parseFloat(s.rate);
    nodes.forEach(function (node) {
      var srcCurrency = node.getAttribute('data-pdp2-ship-source-currency');
      var srcAmount = parseFloat(node.getAttribute('data-pdp2-ship-source-amount'));
      var usdAmount = parseFloat(node.getAttribute('data-pdp2-ship-usd-amount'));

      // Already in the active currency — leave the server-rendered text.
      if (active === srcCurrency) return;
      // No USD anchor amount → can't compute the ROW conversion.
      if (!usdAmount) return;
      // No rate exposed → leave text as-is rather than show $0.
      if (!rate || isNaN(rate) || rate <= 0) return;

      var converted = Math.round(usdAmount * rate);
      var formatted;
      try {
        formatted = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: active,
          maximumFractionDigits: 0,
        }).format(converted);
      } catch (_) {
        // Browser doesn't recognize the currency code — fall back to plain.
        formatted = active + ' ' + converted.toLocaleString();
      }
      node.textContent = formatted;
    });
  }

  // ══════════════════════════════════════════════════════════════════
  // Notify Me — forwards click to the Klaviyo Back-in-Stock button.
  //
  // The OLD PDP used `button.kl-private-reset-css-Xuajs1` as the selector,
  // but that auto-generated class is shared by ~40 Klaviyo elements on
  // the page (signup teaser, close-dialog, etc.) and the FIRST match is
  // the close-dialog button — which is why "popup opens then closes
  // instantly". The BIS button is uniquely identified by
  // `data-a11y-identifier="bis-button-XXXXX"`. Falling back to the
  // BIS container in case Klaviyo changes attribute names.
  // ══════════════════════════════════════════════════════════════════
  function wireNotifyModal(root) {
    var triggers = [].concat(
      Array.from(root.querySelectorAll('[data-pdp2-notify-btn]')),
      Array.from(document.querySelectorAll('[data-pdp2-sticky-notify]'))
    );
    if (!triggers.length) return;
    triggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // 1. Specific BIS button via Klaviyo's data-a11y-identifier.
        var target = document.querySelector('button[data-a11y-identifier^="bis-button"]');
        // 2. BIS container (some Klaviyo versions don't add the a11y attr).
        if (!target) target = document.querySelector('#klaviyo-bis-button-container button');
        // 3. Old-style fallback: any klaviyo-private button that isn't the
        //    close-dialog button. Filter on text to land on "Notify"-style.
        if (!target) {
          var all = Array.from(document.querySelectorAll('button.kl-private-reset-css-Xuajs1'));
          target = all.find(function (b) {
            if (b.classList.contains('klaviyo-close-form')) return false;
            return /notify/i.test(b.textContent || '');
          });
        }
        if (target) target.click();
      });
    });
  }

  // ══════════════════════════════════════════════════════════════════
  // Lucide runtime — replaces every <i data-lucide="<name>"> with
  // the canonical inline SVG for that icon. Defer-loaded UMD; we may
  // boot before the script lands, so retry until window.lucide exists.
  // ══════════════════════════════════════════════════════════════════
  function renderLucide(scope) {
    if (!window.lucide || typeof window.lucide.createIcons !== 'function') return false;
    try {
      window.lucide.createIcons({
        attrs: { width: 22, height: 22, 'stroke-width': 1.25 },
        nameAttr: 'data-lucide',
      });
    } catch (_e) { /* ignore */ }
    return true;
  }
  function bootLucide() {
    if (renderLucide()) return;
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (renderLucide() || tries > 40) clearInterval(iv); // ~4s max
    }, 100);
  }

  // ══════════════════════════════════════════════════════════════════
  // Bootstrap
  // ══════════════════════════════════════════════════════════════════
  onReady(function () {
    document.querySelectorAll('product-info.pdp2-root').forEach(init);
    wireChatLift();
    bootLucide();
    localizeShippingThreshold();
  });
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('product-info.pdp2-root').forEach(init);
    // Re-boot Lucide with retry — covers the quick-add modal case where
    // the lucide UMD script is injected at the same moment as the new
    // product-info markup and may not be ready on the first sweep.
    bootLucide();
  });
})();
